import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaFire, FaEye, FaStar, FaEnvelope, FaHeart, 
  FaUserPlus, FaMapMarkerAlt, FaCheck, FaCircle,
  FaChevronRight, FaChevronLeft, FaClock, FaCamera, FaVideo
} from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext';
import { useDashboardData } from '../../hooks/useDashboardData';
import Loader from '../common/Loader';
import Boxview from '../common/Boxview';
import { useBoxview } from '../../hooks/useBoxview';
import toast from 'react-hot-toast';
import './dashboard.css';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    loading,
    stats,
    hotMatches,
    suggestions,
    visitors,
    recentPhotos,
    error,
    handleLike
  } = useDashboardData();

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;
  const [paginatedMatches, setPaginatedMatches] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [likingId, setLikingId] = useState(null);

  // Boxview hook
  const boxview = useBoxview();
  
  const UPLOADS_URL = 'https://murima.julesferry-ga.com/uploads/';

  // Effet pour la pagination
  useEffect(() => {
    if (hotMatches.length > 0) {
      setTotalPages(Math.ceil(hotMatches.length / itemsPerPage));
      const start = (currentPage - 1) * itemsPerPage;
      const end = start + itemsPerPage;
      setPaginatedMatches(hotMatches.slice(start, end));
    }
  }, [hotMatches, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [hotMatches.length]);

  const statsCards = [
    { 
      icon: FaFire, 
      value: stats.matches, 
      label: 'Matches', 
      gradient: 'linear-gradient(135deg, #ff3b3b, #ff8c42)',
      onClick: () => navigate('/discover') 
    },
    { 
      icon: FaEye, 
      value: stats.views, 
      label: 'Vues', 
      gradient: 'linear-gradient(135deg, #4CAF50, #8BC34A)',
      onClick: () => navigate('/visits') 
    },
    { 
      icon: FaStar, 
      value: stats.favorites, 
      label: 'Favoris', 
      gradient: 'linear-gradient(135deg, #ff8c42, #ffb74d)',
      onClick: () => navigate('/favorites') 
    },
    { 
      icon: FaEnvelope, 
      value: stats.unreadMessages, 
      label: 'Messages', 
      gradient: 'linear-gradient(135deg, #2196F3, #64B5F6)',
      onClick: () => navigate('/messages') 
    }
  ];

  const formatDistance = (km) => {
    if (!km) return 'Distance inconnue';
    if (km < 1) return 'À moins de 1 km';
    if (km === 1) return 'À 1 km';
    return `À ${km} km`;
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diff = Math.floor((now - date) / (1000 * 60));
    
    if (diff < 1) return 'À l\'instant';
    if (diff < 60) return `Il y a ${diff} min`;
    if (diff < 120) return 'Il y a 1h';
    if (diff < 1440) return `Il y a ${Math.floor(diff / 60)}h`;
    return date.toLocaleDateString('fr-FR');
  };

  const getAvatarUrl = (avatar) => {
    if (!avatar) return 'https://images.unsplash.com/photo-1529626455594-4ff0802cfb7e';
    if (avatar.startsWith('http')) return avatar;
    return `${UPLOADS_URL}${avatar}`;
  };

  const openPhotoBoxview = (photos, index) => {
    const imageList = photos.map(photo => ({
      url: photo.url.startsWith('http') ? photo.url : `${UPLOADS_URL}${photo.url}`,
      caption: `Photo de ${photo.user}`
    }));
    boxview.openBoxview(imageList, index);
  };

  // Gestionnaire de like avec vérification
  const handleLikeClick = async (matchId, e) => {
    e.stopPropagation();
    
    // Vérifier que ce n'est pas son propre profil
    if (user?.id === matchId) {
      toast.error('Vous ne pouvez pas liker votre propre profil');
      return;
    }

    setLikingId(matchId);
    try {
      await handleLike(matchId);
    } catch (error) {
      console.error('Erreur lors du like:', error);
    } finally {
      setLikingId(null);
    }
  };

  // Pagination functions
  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToPage = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return (
      <div className="dashboard-error">
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>Réessayer</button>
      </div>
    );
  }

  return (
    <div className="dashboard">
      {/* Section Hero avec stats */}
      <div className="dashboard-hero">
        <div className="hero-content">
          <h1 className="hero-title">
            Bonjour <span className="gradient-text">{user?.pseudo || 'Invité'}</span> !
          </h1>
        </div>
      </div>

      {/* Section Hot Matches avec pagination */}
      {hotMatches.length > 0 && (
        <section className="dashboard-section">
          <div className="section-header">
            <div className="section-title">
              <FaFire className="section-icon" />
              <h2>Hot Matches <span className="matches-count">({hotMatches.length})</span></h2>
            </div>
            <button className="view-all-btn" onClick={() => navigate('/discover')}>
              Voir tout <FaChevronRight />
            </button>
          </div>

          <div className="matches-grid">
            {paginatedMatches.map(match => (
              <div 
                key={match.id} 
                className="match-card"
                onClick={() => navigate(`/profile/${match.id}`)}
              >
                <div className="match-media">
                  <img src={getAvatarUrl(match.avatar)} alt={match.pseudo} />
                  {match.en_ligne && (
                    <span className="online-indicator">
                      <FaCircle />
                    </span>
                  )}
                  <div className="match-distance">
                    <FaMapMarkerAlt /> {formatDistance(match.distance)}
                  </div>
                </div>

                <div className="match-info">
                  <div className="match-header">
                    <h3>{match.pseudo}, {match.age}</h3>
                    <p className="match-location">{match.ville}</p>
                  </div>

                  <p className="match-bio">{match.bio?.substring(0, 80)}...</p>

                  <div className="match-footer">
                    <div className="match-stats">
                      <div className="stat-item">
                        <FaCamera style={{fontSize:'15px'}} className="stat-iconv" />
                        <span className="stat-number">{match.photos || 0}</span>
                        <span className="stat-label" style={{fontSize:'9px'}}>photos</span>
                      </div>
                      <div className="stat-divider"></div>
                      <div className="stat-item">
                        <FaVideo style={{fontSize:'15px'}} className="stat-iconc" />
                        <span className="stat-number">{match.videos || 0}</span>
                        <span className="stat-label" style={{fontSize:'9px'}}>vidéos</span>
                      </div>
                    </div>
                    
                    <div className="match-actions">
                     <button 
    className={`action-btn like-btn ${match.already_liked ? 'liked' : ''}`}
    onClick={(e) => {
      e.stopPropagation();
      handleLike(match.id);
    }}
    disabled={match.already_liked}
  >
    <FaHeart className="btn-icon" />
    <span className="btn-text">
      {match.already_liked ? 'Liké' : 'Liker'}
    </span>
  </button>
                      <button 
                        className="action-btn message-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/messages?user=${match.id}`);
                        }}
                      >
                        <FaEnvelope className="btn-icon" />
                        <span className="btn-text">Message</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination-container">
              <div className="pagination">
                <button 
                  className={`pagination-btn ${currentPage === 1 ? 'disabled' : ''}`}
                  onClick={goToPrevPage}
                  disabled={currentPage === 1}
                >
                  <FaChevronLeft /> Précédent
                </button>
                
                <div className="pagination-pages">
                  {[...Array(totalPages)].map((_, index) => {
                    const page = index + 1;
                    if (
                      page === 1 || 
                      page === totalPages || 
                      (page >= currentPage - 1 && page <= currentPage + 1)
                    ) {
                      return (
                        <button
                          key={page}
                          className={`pagination-page ${currentPage === page ? 'active' : ''}`}
                          onClick={() => goToPage(page)}
                        >
                          {page}
                        </button>
                      );
                    } else if (
                      page === currentPage - 2 || 
                      page === currentPage + 2
                    ) {
                      return <span key={page} className="pagination-dots">...</span>;
                    }
                    return null;
                  })}
                </div>

                <button 
                  className={`pagination-btn ${currentPage === totalPages ? 'disabled' : ''}`}
                  onClick={goToNextPage}
                  disabled={currentPage === totalPages}
                >
                  Suivant <FaChevronRight />
                </button>
              </div>
              
              <div className="pagination-info">
                Page {currentPage} sur {totalPages} • {hotMatches.length} profils au total
              </div>
            </div>
          )}
        </section>
      )}

      {/* Section Suggestions et Visiteurs */}
      <div className="dashboard-grid">
        {/* Suggestions */}
        {suggestions.length > 0 && (
          <section className="dashboard-section suggestions-section">
            <div className="section-header">
              <div className="section-title">
                <FaUserPlus className="section-icon" />
                <h2>Suggestions pour vous</h2>
              </div>
              <button className="view-all-btn" onClick={() => navigate('/discover')}>
                Voir tout
              </button>
            </div>

            <div className="suggestions-list">
              {suggestions.map(suggestion => (
                <div 
                  key={suggestion.id} 
                  className="suggestion-item"
                  onClick={() => navigate(`/profile/${suggestion.id}`)}
                >
                  <div className="suggestion-avatar">
                    <img src={getAvatarUrl(suggestion.avatar)} alt={suggestion.pseudo} />
                    {suggestion.en_ligne && (
                      <span className="online-dot"></span>
                    )}
                  </div>
                  <div className="suggestion-content">
                    <div className="suggestion-header">
                      <h4>{suggestion.pseudo}, {suggestion.age}</h4>
                      {suggestion.is_verified && (
                        <span className="verified-badge-small">
                          <FaCheck />
                        </span>
                      )}
                    </div>
                    <p className="suggestion-location">{suggestion.ville}</p>
                    <div className="suggestion-tags">
                      {suggestion.interests?.slice(0, 2).map((tag, i) => (
                        <span key={i} className="tag">{tag}</span>
                      ))}
                    </div>
                  </div>
                  <div className="suggestion-match">
                    <span className="match-percent">{suggestion.match}%</span>
                    <span className="match-label">match</span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Visiteurs récents */}
        {visitors.length > 0 && (
          <section className="dashboard-section visitors-section">
            <div className="section-header">
              <div className="section-title">
                <FaEye className="section-icon" />
                <h2>Visiteurs récents</h2>
              </div>
              <button className="view-all-btn" onClick={() => navigate('/visits')}>
                Voir tout
              </button>
            </div>

            <div className="visitors-list">
              {visitors.map(visitor => (
                <div 
                  key={visitor.id} 
                  className="visitor-item"
                  onClick={() => navigate(`/profile/${visitor.id}`)}
                >
                  <div className="visitor-avatar">
                    <img src={getAvatarUrl(visitor.avatar)} alt={visitor.pseudo} />
                    {visitor.en_ligne && (
                      <span className="online-dot"></span>
                    )}
                  </div>
                  <div className="visitor-info">
                    <h4>{visitor.pseudo}, {visitor.age}</h4>
                    <p className="visit-time">
                      <FaClock /> {formatTimeAgo(visitor.date_visite)}
                    </p>
                  </div>
                  <button 
                    className="btn-message-small"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/messages?user=${visitor.id}`);
                    }}
                  >
                    <FaEnvelope />
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>

      {/* Section Photos populaires */}
      {recentPhotos.length > 0 && (
        <section className="dashboard-section photos-section">
          <div className="section-header">
            <div className="section-title">
              <FaCamera className="section-icon" />
              <h2>Photos populaires</h2>
            </div>
            <button className="view-all-btn" onClick={() => navigate('/photos')}>
              Voir tout
            </button>
          </div>

          <div className="photos-grid">
            {recentPhotos.map((photo, index) => (
              <div 
                key={photo.id} 
                className="photo-card"
                onClick={() => openPhotoBoxview(recentPhotos, index)}
              >
                <img src={photo.url} alt={`Photo de ${photo.user}`} />
                <div className="photo-overlay">
                  <div className="photo-info">
                    <span className="photo-user">{photo.user}</span>
                    <span className="photo-likes">
                      <FaHeart /> {photo.likes}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Boxview pour les photos */}
      <Boxview 
        isOpen={boxview.isOpen}
        onClose={boxview.closeBoxview}
        images={boxview.images}
        initialIndex={boxview.currentIndex}
      />
    </div>
  );
};

export default Dashboard;